export enum ClockingTimeLabel {
  ANYTIME = 'anytime',
  DAY = 'day',
  NIGHT = 'night',
  EVENING_AND_WEEKEND = 'evening and weekend',
  HEATING = 'heating',
}

export enum TimeOfUseLabel {
  anytime = 'anytime',
  offpeak = 'offpeak',
  peak = 'peak',
  heating = 'heating',
  eveningAndWeekend = 'evening and weekend',
}

type Pounds = number;
type Pence = number;
type IsoDateString = string;
type EpochDays = number;

export enum ContractType {
  FIXED = 'fixed',
  VARIABLE = 'variable',
}

export enum CustomerContractType {
  FIXED = 'Fixed',
  VARIABLE = 'Variable',
  MIXED = 'Mixed',
  UNKNOWN = 'Unknown',
}

export type UnitRates = Array<{
  unitRate: Pounds;
  clockingTimeLabel: ClockingTimeLabel;
  timeOfUseLabel?: TimeOfUseLabel;
}>;

export enum ContractFuel {
  ELEC = 'electricity',
  GAS = 'gas',
}

export interface EnergyContractResponse {
  contractId: string;
  meterType: string | null;
  mpxn: string;
  msn: string | null;
  relatedSupplies?: string[];
  lossDate: null | IsoDateString;
  profileClass: string;
  startDate: EpochDays;
  paymentMethod: 'Direct Debit' | 'Prepayment' | string; // Preserving string for backwards compatibility
  expiryDate: EpochDays | null; // null for variable rate
  type: ContractType;
  fuel: ContractFuel;
  standingRate: Pounds;
  exitFee: Pence;
  planName: string;
  serviceAccountId: string;
  lengthInMonths: number | null;
  region: string;
  unitRates: UnitRates;
  status: {
    active: boolean;
    inRenewal: boolean;
    inLoss: boolean;
    lossComplete: boolean;
    hasFutureContracts: boolean;
  };
}

export interface EnergyContracts {
  electricity: EnergyContractResponse[];
  gas: EnergyContractResponse[];
}

// Interfaces derived from the Zod Schema definitions
// https://github.com/ovotech/core-contracts/blob/f2233d137496d6d849b79fa9e4f06a82bf4f7c50/shared/api-contract/src/v1/response.ts

interface Money {
  /** A valid ISO-4217 currency code. Example: "GBP" */
  currency: string;
  /** The numerical amount of money in major units. */
  value: string;
}

interface ContractStatus {
  /** Are we currently in the time period covered by this contract. */
  active: boolean;
  /** Are there more recent contracts attached to this account? */
  hasFutureContracts: boolean;
  /** The supply covered by this contract will be lost in the future (on lossDate). */
  inLoss?: boolean;
  /** The supply covered by this contract was lost (on lossDate). */
  lossComplete?: boolean;
  /** This contract is eligible for renewal (not a loss, and within renewal window (53 days)) */
  inRenewal?: boolean;
}

interface ConsumptionRates {
  /** The label for consumption at the default rate. Example: "anytime" */
  defaultRateLabel: string;
  /** The timing category for these rates. Example: ["peak", "offpeak", "anytime", "solar"] */
  timingCategory: string;
  /** The unit rate per kWh in the major local currency (to 6dp). This applies to any usage outside of inclining/declining blocks. */
  defaultRate: Money;
}

interface RegionMapping {
  /** The region mapping code. Example: "State" */
  code: string;
  /** The region where this tariff is available. Example: "VIC" */
  value: string;
}

interface Meter {
  /** Serial number of the meter. */
  msn?: string;
  /** Profile class of the meter. Example: "01" */
  profileClass?: string;
  /** Type of the meter. Example: "S2" */
  type?: string;
}

export interface LatestTariff {
  /** The unique ID for this tariff. */
  id: string;
  /** Name of this tariff. Example: "Simpler Energy" */
  name: string;
  /** Type of this tariff. Enum: "FIXED" | "VARIABLE" */
  type: 'FIXED' | 'VARIABLE';
  /** The date (inclusive) from which the tariff is active. Example: "2025-06-20" */
  startDate: string;
  /** The date (inclusive) to which the tariff is active. */
  endDate?: string;
  /** The duration of a tariff in months, for variable tariffs this will not be present. */
  lengthInMonths?: number;
  /** The mechanism by which the customer pays for the tariff. Enum: "OTHER" | "PAYG" | "PAYM" */
  paymentMechanism: 'OTHER' | 'PAYG' | 'PAYM';
  /** Method of payment. Enum: "DIRECT_DEBIT" | "ON_DEMAND" | "PREPAYMENT" */
  paymentMethod: 'DIRECT_DEBIT' | 'ON_DEMAND' | 'PREPAYMENT';
  /** The ID of the tariff family. This groups similar tariffs together. */
  familyId: string;
  /** Information about the geographical region where the tariff is valid. */
  regionMapping: RegionMapping;
  /** The exit fee. */
  exitFee: Money;
  /** The standing rate (6 decimals precision) per day of the tariff. This value is exclusive of VAT. Example: "VAT" */
  standingRate: Money;
  /** Information about consumption charge rates for the tariff. */
  consumptionChargeRates: ConsumptionRates[];
}

export interface EnergyContractDetails {
  /** The same as meta.endDate, if the supply is ending due to a loss. Example: "2026-01-14" */
  lossDate?: string;
  /** The fuel for which there is a contract. Enum: "ELECTRICITY" | "GAS" */
  fuel: 'ELECTRICITY' | 'GAS';
  /** Meter Point Number for the supply point this contract is for. Example: "9999999127811" */
  mpxn: string;
  /** Related meter points (MPXN) for which the contract has been issued. */
  relatedSupplies: string[];
  /** Technical details of the meter attached to this supply. */
  meter: Meter;
  /** Calculated information about the current status of this contract. */
  status: ContractStatus;
  /** Most up-to-date tariff (pricing information) for this contract. */
  latestTariff: LatestTariff;
}

export interface Contract {
  /** The identifier for this contract in the context of the system that holds it. */
  contractId: string;
  /** ID of the account relating to this contract in the underlying system. Example: "1234556" */
  accountId: string;
  /** Start date of the contract. Example: "2025-01-15" */
  startDate: string;
  /** End date of the contract if it has one. Example: "2026-01-14" */
  endDate?: string;
  /** A descriptive name that can be used to informally identify what the contract is for. Example: "Simpler Energy" */
  name: string;
  type: 'energy';
  /** Information about contract details. */
  details: EnergyContractDetails;
}

export interface GetCustomerContractsResponse {
  data: Contract[];
}
